
import { Component } from 'vue-property-decorator';
import { mixins } from 'vue-class-component';
import moment from 'moment';
import VWrapper from '@/components/shared/VWrapper.vue';
import VTable from '@/components/shared/table/VTable.vue';
import MerchantStatusChip from '@/components/smoothrPay/MerchantStatusChip.vue';
import MerchantConfigurationDialog from '@/components/smoothrPay/MerchantConfigurationDialog.vue';
import Notification from '@/mixins/Notification';
import api from '@/api/http/SmoothrPayApiService';
import {
  CreateMerchantConfigurationRequest,
  MerchantAccount,
  MerchantConfiguration,
} from '@/interfaces/models/SmoothrPay';

/**
 * Smoothr Pay merchants. The list is scoped server-side: a regular user only
 * ever sees the configurations they own, so the Owner column earns its place
 * for admins alone.
 */
@Component({
  components: { VWrapper, VTable, MerchantStatusChip, MerchantConfigurationDialog },
})
export default class SmoothrPay extends mixins(Notification) {
  public items: MerchantConfiguration[] = [];
  public merchantAccounts: MerchantAccount[] = [];
  public total: number = 0;
  public page: number = 1;
  public dialogVisible: boolean = false;

  /**
   * The Owner column only earns its place for admins — a regular user sees
   * nothing but their own configurations.
   */
  get headers() {
    const headers: any[] = [{ text: 'smoothrPay.columns.merchant', value: 'legalEntity', type: 'slot', virtual: true }];
    if (this.$isAdmin()) {
      headers.push({ text: 'smoothrPay.columns.owner', value: 'owner', type: 'slot', virtual: true });
    }
    headers.push(
      { text: 'smoothrPay.columns.status', value: 'status', type: 'slot', virtual: true },
      { text: 'smoothrPay.columns.verification', value: 'verification', type: 'slot', virtual: true },
      { text: 'smoothrPay.columns.createdAt', value: 'createdAt', type: 'slot', virtual: true },
    );
    return headers;
  }

  public mounted() {
    this.fetch();
    this.fetchMerchantAccounts();
  }

  public onPage(event: { page: number }) {
    this.page = event.page;
    this.fetch();
  }

  public formatDate(value?: string): string {
    return value ? moment(value).format('DD.MM.YYYY') : '—';
  }

  /** What Adyen currently allows — the reason to open a configuration at all. */
  public verificationText(item: MerchantConfiguration): string {
    const verification = item.verification;
    if (!verification) {
      return '—';
    }
    const parts: string[] = [];
    if (verification.payInAllowed) {
      parts.push(this.$t('smoothrPay.verification.payIn') as string);
    }
    if (verification.payOutAllowed) {
      parts.push(this.$t('smoothrPay.verification.payOut') as string);
    }
    if (verification.outstandingProblems) {
      parts.push(`${verification.outstandingProblems} ${this.$t('smoothrPay.verification.problems')}`);
    }
    return parts.length > 0 ? parts.join(' · ') : '—';
  }

  public open(item: MerchantConfiguration) {
    this.$router.push({ name: 'smoothrPay.detail', params: { id: item._id } });
  }

  public async fetch() {
    this.$startLoading('smoothrPay');
    try {
      const { data } = await api.getConfigurations({ page: this.page, limit: 10 });
      this.items = data.data || [];
      this.total = data.total || 0;
    } catch (error) {
      this.notifyError('smoothrPay.notification.loadError');
    } finally {
      this.$stopLoading('smoothrPay');
    }
  }

  /** Only used to offer a non-default Adyen account on the create dialog. */
  public async fetchMerchantAccounts() {
    if (!this.$isAdmin()) {
      return;
    }
    try {
      const { data } = await api.getMerchantAccounts();
      this.merchantAccounts = data || [];
    } catch (error) {
      this.merchantAccounts = [];
    }
  }

  public async create(payload: CreateMerchantConfigurationRequest) {
    this.$startLoading('smoothrPay.create');
    try {
      const { data } = await api.createConfiguration(payload);
      this.dialogVisible = false;
      this.notifySuccess('smoothrPay.notification.created');
      this.$router.push({ name: 'smoothrPay.detail', params: { id: data._id } });
    } catch (error) {
      this.notifyError('smoothrPay.notification.createError');
    } finally {
      this.$stopLoading('smoothrPay.create');
    }
  }
}
